/**
 * Tablero operativo y reportes del despacho (PR 7).
 * Pestañas reducidas: Vencidos / Esta semana / Tareas / Clientes incompletos.
 * La API backend sigue aceptando los reportes antiguos (export/CSV); solo se ocultan en UI.
 */
import api from "@/lib/api";

function downloadBlobResponse(response, fallbackName) {
  const blob = response.data;
  const disposition = response.headers?.["content-disposition"] || "";
  const match = disposition.match(/filename="([^"]+)"/);
  const filename = match?.[1] || fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Enlaces antiguos (?report=…) → pestaña/ruta vigente.
 * No elimina endpoints; evita 404 visual al llegar desde Asistente o bookmarks.
 */
export const OPERATIONAL_REPORT_ALIASES = {
  alerts_reminders: { tab: null, href: "/despacho/calendario?tab=auto-alertas&sub=tablero" },
  obligations_by_client: { tab: "overdue" },
  compliance_by_obligation: { tab: "overdue" },
  not_applicable_this_month: { tab: "overdue" },
  tasks_by_auxiliar: { tab: null, href: "/despacho/tareas" },
  client_messages: { tab: null, href: "/despacho/mensajes" },
};

/** KPIs clicables del tablero / inicio. */
export const OPERATIONAL_DASHBOARD_CARDS = [
  { key: "overdue_instances", label: "Obligaciones vencidas", report: "overdue", params: {}, tone: "warn", auxPrimary: true },
  { key: "due_this_week", label: "Vencen esta semana", report: "upcoming_due_dates", params: { days: 7 }, tone: "info", auxPrimary: true },
  { key: "pending_tasks", label: "Tareas pendientes", report: "pending_tasks", params: {}, tone: "neutral", auxPrimary: true },
  { key: "clients_no_csf", label: "Clientes sin CFS", report: "incomplete_clients", params: { fiscal_status_badge: "falta_cfs" }, tone: "critical", auxPrimary: true },
  { key: "clients_no_obligations", label: "Clientes sin obligaciones", report: "incomplete_clients", params: { fiscal_status_badge: "sin_obligaciones" }, tone: "critical", auxPrimary: true },
];

/**
 * Pestañas visibles del tablero (auxiliar y supervisor).
 * Reportes de supervisión (por auxiliar, alertas, etc.) viven en Inicio / Asistente / Calendario / Tareas.
 */
export const OPERATIONAL_REPORT_TABS = [
  { id: "overdue", label: "Vencidos", auxPrimary: true },
  { id: "upcoming_due_dates", label: "Esta semana", auxPrimary: true },
  { id: "pending_tasks", label: "Tareas pendientes", auxPrimary: true },
  { id: "incomplete_clients", label: "Clientes incompletos", auxPrimary: true },
];

export function resolveOperationalReportTab(reportId) {
  const raw = String(reportId || "").trim();
  if (!raw) return { tab: "overdue" };
  const visible = new Set(OPERATIONAL_REPORT_TABS.map((t) => t.id));
  if (visible.has(raw)) return { tab: raw };
  const alias = OPERATIONAL_REPORT_ALIASES[raw];
  if (alias) return { tab: alias.tab || "overdue", href: alias.href || null };
  return { tab: "overdue" };
}

export function filterOperationalTabsForScope(tabs, { canViewTeamReports = false } = {}) {
  return (tabs || []).filter((t) => {
    if (t.teamOnly && !canViewTeamReports) return false;
    if (!canViewTeamReports && t.supervisorExtra) return false;
    if (!canViewTeamReports && t.auxPrimary === false) return false;
    return true;
  });
}

export function filterOperationalCardsForScope(cards, { canViewTeamReports = false } = {}) {
  return (cards || []).filter((c) => {
    if (canViewTeamReports) return true;
    return c.auxPrimary !== false;
  });
}

export function createOperationalReportsApi(httpClient = api) {
  return {
    async getDashboard(params = {}) {
      const { data } = await httpClient.get("/despacho/operational-dashboard", { params });
      return data;
    },

    async getReport(report, params = {}) {
      const { data } = await httpClient.get("/despacho/operational-reports", {
        params: { report, ...params },
      });
      return data;
    },

    async exportReport(report, params = {}) {
      const response = await httpClient.get("/despacho/operational-reports/export", {
        params: { report, ...params },
        responseType: "blob",
      });
      downloadBlobResponse(response, `despacho_${report}.csv`);
    },
  };
}

export const operationalReportsApi = createOperationalReportsApi();
